import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getBusinessDevelopmentData, saveBusinessDevelopmentForm } from '../../../../../../api/transitions';
import { getProgramsList } from '../../../../../../redux/slices/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import { trimData } from '../../../../../../utils/formatText';
import SkeletonBlock from '../../../SkeletonBlock';
import { flattenTransitionSubmitData } from '../../../formDataUtils';
import { BUSINESS_DEVELOPMENT } from '../../../formQuestionsEnum';
import FacilityProgramsForm from './FacilityProgramsForm';
import GeneralInfoForm from './GeneralInfoForm';
import MarketingProgramsForm from './MarketingProgramsForm';
import ReferralSoftwareForm from './ReferralSoftwareForm';

const BusinessDevelopmentView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);
  const { watch } = useFormContext();
  const companyId = watch('companyId');

  const businessDevelopmentPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.businessDevelopmentFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getBusinessDevelopmentData(id, facilityId ?? 0)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(BUSINESS_DEVELOPMENT).map(([, v]) => [v.id, v.fieldName]));

    const mappedFieldsRaw = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    const defaultNamedRow = { id: 0, name: '', deleteFlag: false };
    const defaultNameWithFilesRow = { id: 0, name: '', files: [], deleteFlag: false };

    const mappedFields = {
      ...mappedFieldsRaw,
      // Keep a default empty row so a blank row will show before any input.
      referringHospitals:
        mappedFieldsRaw.referringHospitals?.length > 0 ? mappedFieldsRaw.referringHospitals : [defaultNamedRow],
      homeHealthAgencies:
        mappedFieldsRaw.homeHealthAgencies?.length > 0 ? mappedFieldsRaw.homeHealthAgencies : [defaultNamedRow],
      hospiceAgencies:
        mappedFieldsRaw.hospiceAgencies?.length > 0 ? mappedFieldsRaw.hospiceAgencies : [defaultNameWithFilesRow],
      referralSoftwares:
        mappedFieldsRaw.referralSoftwares?.length > 0 ? mappedFieldsRaw.referralSoftwares : [defaultNameWithFilesRow],
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [setFormData, data]);

  const submitForm = async (allData) => {
    const flattenData = flattenTransitionSubmitData(allData, undefined, ['bedCount']);
    const result = trimData(flattenData);

    await saveBusinessDevelopmentForm(
      {
        id,
        sellerFacilityId: facilityId,
        outsourcedCompanyId: data.outsourcedCompanyId,
        ...result,
      },
      companyId
    )
      .then((resp) => {
        enqueueSnackbar('Business Development data saved successfully', { variant: 'success' });
        setData(resp.data);
        dispatch(getProgramsList(companyId));
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Business Development data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      <GeneralInfoForm permissions={businessDevelopmentPermissions} />
      <FacilityProgramsForm permissions={businessDevelopmentPermissions} />
      <MarketingProgramsForm permissions={businessDevelopmentPermissions} />
      <ReferralSoftwareForm permissions={businessDevelopmentPermissions} />
    </>
  );
});

export default BusinessDevelopmentView;

BusinessDevelopmentView.propTypes = {
  setFormData: PropTypes.func,
};
